import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { ChequeSelectorProps, ChequeSelectorRef } from "./types";
import { Column, Row, ScrollRow } from "../../containers";
import { TextInput } from "../../inputs";
import { Cheque } from "../../../types";
import LilCheque from "../lil-cheque";
import { useThemeStore } from "../../../stores";
import { AlignItems, JustifyContent } from "../../containers/types";
import { CheckIcon, TrashIcon } from "../../../assets/icons";
import { IconAnimation } from "../../animation";
import { Heading } from "../../typography";

const ChequeSelector = forwardRef<ChequeSelectorRef, ChequeSelectorProps>(
  (
    {
      items,
      selectedItems,
      setSelectedItems,
      label,
      canDel = false,
      className = "",
      props,
      style = {},
    },
    ref
  ) => {
    const [searchValue, setSearchValue] = useState<string>("");
    const [_items, setItems] = useState<Array<Cheque>>([]);

    useImperativeHandle(ref, () => ({
      setSearchValue: (value: string): void => {
        setSearchValue(value);
      },
      clear: () => {
        setItems([]);
        setSearchValue("");
        setSelectedItems([]);
      },
    }));

    const theme = useThemeStore((s) => s.theme);

    useEffect(() => {
      if (typeof items === "object") setItems(items);
      else {
        let tmp: Array<Cheque> = [];
        selectedItems.forEach((s) => tmp.push(s.cheque));
        setItems(tmp);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items]);

    const onSelect = (cheque: Cheque, selected: boolean): void => {
      setSelectedItems((prev) => {
        if (selected) {
          prev = prev.filter((i) => i.cheque._id.$oid !== cheque._id.$oid);
        } else {
          prev.push({ cheque, delete: false });
        }
        return [...prev];
      });
    };

    const onDelete = (id: string): void => {
      setSelectedItems((prev) => {
        for (let i = 0; i < prev.length; i++) {
          if (prev[i].cheque._id.$oid !== id) continue;
          prev[i] = { ...prev[i], delete: !prev[i].delete };
          break;
        }
        return [...prev];
      });
    };

    useEffect(() => {
      if (!searchValue) {
        if (typeof items === "object") setItems(items);
        else {
          let tmp: Array<Cheque> = [];
          selectedItems.forEach((s) => tmp.push(s.cheque));
          setItems(tmp);
        }
      } else {
        if (typeof items === "object") {
          let res = items.filter((c) =>
            c.number.toLowerCase().includes(searchValue.toLowerCase())
          );
          for (let i = 0; i < selectedItems.length; i++) {
            let tmp = res.filter(
              (r) => r._id.$oid === selectedItems[i].cheque._id.$oid
            );
            if (!tmp.length) {
              res.push(selectedItems[i].cheque);
            }
          }
          setItems(res);
        } else {
          items(searchValue)
            .then((res) => {
              for (let i = 0; i < selectedItems.length; i++) {
                let tmp = res.filter(
                  (r) => r._id.$oid === selectedItems[i].cheque._id.$oid
                );
                if (!tmp.length) {
                  res.push(selectedItems[i].cheque);
                }
              }
              setItems(res);
            })
            .catch((err) => {
              console.log(err);
            });
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue, selectedItems]);

    return (
      <Column
        gap={10}
        style={{ width: "100%", ...style }}
        props={props}
        className={className}
        alignItems={AlignItems.Start}
      >
        <TextInput
          label={label}
          value={searchValue}
          inputProps={{ placeholder: "جستجو با شماره چک" }}
          setValue={setSearchValue}
          style={{ maxWidth: 300 }}
        />

        <ScrollRow
          gap={10}
          height={200}
          background={theme.background}
          style={{ borderRadius: 15, position: "relative" }}
        >
          {!_items.length ? (
            <Heading
              textStyles={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }}
              color={theme.textOnBg}
            >
              چکی یافت نشد!
            </Heading>
          ) : (
            <></>
          )}
          {_items.map((c) => {
            let selected: any = selectedItems.filter(
              (item) => item.cheque._id.$oid === c._id.$oid
            );
            if (selected.length) selected = selected[0];
            else selected = null;
            return (
              <Column key={c._id.$oid} style={{ position: "relative" }}>
                <LilCheque {...c} style={{ margin: "0 0.5rem" }} />
                <Column
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                  }}
                >
                  <Row
                    style={{
                      width: 20,
                      height: 20,
                      border: `1px solid ${
                        selected ? theme.primary : theme.icon
                      }`,
                      borderRadius: 7,
                      position: "absolute",
                      top: 10,
                      left: 20,
                      cursor: "pointer",
                    }}
                    justifyContent={JustifyContent.Center}
                    className="transition"
                    props={{ onClick: () => onSelect(c, !!selected) }}
                  >
                    {selected ? <CheckIcon color={theme.primary} /> : <></>}
                  </Row>
                  {canDel && selected ? (
                    <IconAnimation
                      Icon={TrashIcon}
                      onHoverColor={theme.alert}
                      color={selected.delete ? theme.alert : theme.icon}
                      width={24}
                      height={24}
                      style={{
                        position: "absolute",
                        left: 20,
                        top: 45,
                        cursor: "pointer",
                      }}
                      onClick={() => onDelete(c._id.$oid)}
                    />
                  ) : (
                    <></>
                  )}
                </Column>
              </Column>
            );
          })}
        </ScrollRow>
      </Column>
    );
  }
);

export default ChequeSelector;
